import type { SummaryDraftScope } from './summaryEditorPosition';
import type { SummaryStreamCheckpoint } from './summaryEditorUpdates';

export type SummaryDraft = { version: 1; base: string; content: string; document?: object; uncertain?: boolean; stream?: SummaryStreamCheckpoint };
export interface SummaryDraftStore {
    read(): Promise<SummaryDraft | null>;
    write(draft: SummaryDraft | null): Promise<void>;
}
export class SummaryDraftConflictError extends Error {}
type RecordValue = { revision: string; iv?: Uint8Array; encrypted?: ArrayBuffer };
const operations = new Map<string, Promise<unknown>>();
function serialize<T>(key: string, run: () => Promise<T>): Promise<T> {
    const result = (operations.get(key) ?? Promise.resolve()).catch(() => {}).then(run);
    operations.set(key, result);
    const clear = () => { if (operations.get(key) === result) operations.delete(key); };
    void result.then(clear, clear);
    return result;
}
const scopeKey = (scope: SummaryDraftScope) => JSON.stringify([scope.domain, scope.userId, scope.recordingId, scope.meetingType]);

/** Ciphertext and a non-extractable key live in IndexedDB, never in localStorage.
 * Same-origin scripts can use the key; this is browser at-rest protection, not an OS keychain.
 */
export function createSummaryDraftStore(scope: SummaryDraftScope): SummaryDraftStore {
    let revision: string | undefined;
    let database: Promise<IDBDatabase> | undefined;
    const id = scopeKey(scope);
    const owner = JSON.stringify([scope.domain, scope.userId]);
    const open = () => database ??= new Promise((resolve, reject) => {
        if (!scope.userId || !globalThis.indexedDB || !globalThis.crypto?.subtle) {
            reject(new Error('Encrypted browser storage is unavailable')); return;
        }
        const request = indexedDB.open('notizy-summary-drafts-v1', 1);
        request.onupgradeneeded = () => {
            request.result.createObjectStore('drafts'); request.result.createObjectStore('keys');
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('Draft database is blocked'));
        request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    });
    const readValue = async <T,>(store: string, key: string): Promise<T | undefined> => {
        const db = await open();
        return new Promise((resolve, reject) => {
            const request = db.transaction(store).objectStore(store).get(key);
            request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
        });
    };
    const getKey = async (): Promise<CryptoKey> => {
        const existing = await readValue<CryptoKey>('keys', owner);
        if (existing) return existing;
        const generated = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
        const db = await open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction('keys', 'readwrite');
            const keys = tx.objectStore('keys'); const request = keys.get(owner);
            let key = generated;
            request.onsuccess = () => { if (request.result) key = request.result; else keys.put(generated, owner); };
            tx.oncomplete = () => resolve(key); tx.onabort = () => reject(tx.error);
        });
    };
    const store: SummaryDraftStore = {
        async read() {
            const record = await readValue<RecordValue>('drafts', id);
            revision = record?.revision;
            if (!record?.encrypted || !record.iv) return null;
            const clear = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv, additionalData: new TextEncoder().encode(id) }, await getKey(), record.encrypted);
            const draft: SummaryDraft = JSON.parse(new TextDecoder().decode(clear));
            if (draft.version !== 1 || typeof draft.base !== 'string' || typeof draft.content !== 'string' ||
                (draft.document !== undefined && (!draft.document || typeof draft.document !== 'object'))) throw new Error('Invalid draft');
            return draft;
        },
        async write(draft) {
            const next: RecordValue = { revision: crypto.randomUUID() };
            if (draft) {
                next.iv = crypto.getRandomValues(new Uint8Array(12));
                next.encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: next.iv, additionalData: new TextEncoder().encode(id) },
                    await getKey(), new TextEncoder().encode(JSON.stringify(draft)));
            }
            const db = await open();
            await new Promise<void>((resolve, reject) => {
                const tx = db.transaction('drafts', 'readwrite'); const drafts = tx.objectStore('drafts');
                const request = drafts.get(id); let conflict = false;
                request.onsuccess = () => {
                    if (request.result?.revision !== revision) { conflict = true; tx.abort(); return; }
                    // Keep a tombstone revision so a stale tab cannot recreate a deleted draft.
                    drafts.put(next, id);
                };
                tx.oncomplete = () => { revision = next.revision; resolve(); };
                tx.onabort = () => reject(conflict ? new SummaryDraftConflictError('Draft changed in another tab') : tx.error);
            });
        },
    };
    return { read: () => serialize(id, () => store.read()), write: draft => serialize(id, () => store.write(draft)) };
}
